import React, { useState, useEffect, useRef } from 'react';
import {
    listFolderImages, uploadItemImage,
    fetchProductOffer, fetchProductInfo,
    addOffer, updateOffer, deleteOffers,
    addStoreProduct, updateStoreProduct, deleteStoreProducts,
} from '../../bridge';
import UpgradeModal from './UpgradeModal';
import ManagedServiceBanner from './ManagedServiceBanner';

const FREE_ITEMS = 5;
const PAID_ITEMS = 20;

// ── Offer card ────────────────────────────────────────────────────────────────
const OfferCard = ({ item, currency, onSave, onDelete }) => {
    const [name, setName] = useState(item.product_name || '');
    const [price, setPrice] = useState(item.product_price || '');
    const [previousPrice, setPreviousPrice] = useState(item.previous_product_price || '');
    const [description, setDescription] = useState(item.product_description || '');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleSave = async () => { setSaving(true); await onSave(item.imageIndex, { name, price, previousPrice, description }); setSaving(false); };
    const handleDelete = async () => { setDeleting(true); await onDelete(item.imageIndex); };

    return (
        <div className="item-card">
            <img src={item.url} alt={name || 'Offer'} className="item-card-image" />
            <div className="item-card-body">
                <input className="item-card-field" value={name} onChange={e => setName(e.target.value)} placeholder="Product name" />
                <div style={{ display: 'flex', gap: 6 }}>
                    <input className="item-card-field" value={price} onChange={e => setPrice(e.target.value)} placeholder={`Price (${currency})`} style={{ flex: 1 }} />
                    <input className="item-card-field" value={previousPrice} onChange={e => setPreviousPrice(e.target.value)} placeholder="Was" style={{ flex: 1 }} title="Previous price (shown crossed out)" />
                </div>
                <textarea className="item-card-field textarea" value={description} onChange={e => setDescription(e.target.value)} placeholder="Description" />
                <div className="item-card-actions">
                    <button className="small-btn primary" onClick={handleSave} disabled={saving || deleting}>{saving ? '…' : 'Save'}</button>
                    <button className="small-btn danger" onClick={handleDelete} disabled={saving || deleting}>{deleting ? '…' : 'Delete'}</button>
                </div>
            </div>
        </div>
    );
};

// ── Product card ──────────────────────────────────────────────────────────────
const ProductCard = ({ item, currency, onSave, onDelete }) => {
    const [name, setName] = useState(item.product_name || '');
    const [price, setPrice] = useState(item.product_price || '');
    const [description, setDescription] = useState(item.product_description || '');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleSave = async () => { setSaving(true); await onSave(item.imageIndex, name, price, description); setSaving(false); };
    const handleDelete = async () => { setDeleting(true); await onDelete(item.imageIndex); };

    return (
        <div className="item-card">
            <img src={item.url} alt={name || 'Product'} className="item-card-image" />
            <div className="item-card-body">
                <input className="item-card-field" value={name} onChange={e => setName(e.target.value)} placeholder="Product name" />
                <input className="item-card-field" value={price} onChange={e => setPrice(e.target.value)} placeholder={`Price (${currency})`} />
                <textarea className="item-card-field textarea" value={description} onChange={e => setDescription(e.target.value)} placeholder="Description" />
                <div className="item-card-actions">
                    <button className="small-btn primary" onClick={handleSave} disabled={saving || deleting}>{saving ? '…' : 'Save'}</button>
                    <button className="small-btn danger" onClick={handleDelete} disabled={saving || deleting}>{deleting ? '…' : 'Delete'}</button>
                </div>
            </div>
        </div>
    );
};

// ── Main StoreManager ─────────────────────────────────────────────────────────
const StoreManager = ({ email, currency, isSubscribed }) => {
    const offersInputRef = useRef(null);
    const productsInputRef = useRef(null);
    const [offers, setOffers] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadingOffers, setUploadingOffers] = useState(false);
    const [uploadingProducts, setUploadingProducts] = useState(false);
    const [showUpgrade, setShowUpgrade] = useState(false);

    useEffect(() => { loadAll(); }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadAll = async () => {
        setLoading(true);
        const [offerImages, productImages] = await Promise.all([listFolderImages(email, 1), listFolderImages(email, 2)]);
        const offersWithInfo = await Promise.all(offerImages.map(async img => {
            const info = await fetchProductOffer({ userEmail: email, categoryId: 1, imageIndex: img.name });
            return { imageIndex: img.name, url: img.url, product_name: info?.product_name || '', product_price: info?.product_price || '', previous_product_price: info?.previous_product_price || '', product_description: info?.product_description || '' };
        }));
        const productsWithInfo = await Promise.all(productImages.map(async img => {
            const info = await fetchProductInfo({ userEmail: email, categoryId: 2, imageIndex: img.name });
            return { imageIndex: img.name, url: img.url, product_name: info?.product_name || '', product_price: info?.product_price || '', product_description: info?.product_description || '' };
        }));
        setOffers(offersWithInfo);
        setProducts(productsWithInfo);
        setLoading(false);
    };

    const checkLimit = (currentCount, setter) => {
        if (currentCount >= FREE_ITEMS && !isSubscribed) { setShowUpgrade(true); return false; }
        if (currentCount >= PAID_ITEMS) return false;
        return true;
    };

    const handleAddOffer = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingOffers(true);
        const filename = await uploadItemImage(email, 1, file);
        if (filename) {
            await addOffer(email, filename, '', '', '', '');
            const url = `https://yeybluihiblysywmazen.supabase.co/storage/v1/object/public/images/${email}/1/${filename}`;
            setOffers(prev => [...prev, { imageIndex: filename, url, product_name: '', product_price: '', previous_product_price: '', product_description: '' }]);
        }
        setUploadingOffers(false);
        e.target.value = '';
    };

    const handleSaveOffer = async (imageIndex, data) => {
        await updateOffer(email, imageIndex, data);
        setOffers(prev => prev.map(o => o.imageIndex === imageIndex ? { ...o, product_name: data.name, product_price: data.price, previous_product_price: data.previousPrice, product_description: data.description } : o));
    };

    const handleDeleteOffer = async (imageIndex) => {
        await deleteOffers(email, [imageIndex]);
        setOffers(prev => prev.filter(o => o.imageIndex !== imageIndex));
    };

    const handleAddProduct = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingProducts(true);
        const filename = await uploadItemImage(email, 2, file);
        if (filename) {
            await addStoreProduct(email, filename, '', '', '');
            const url = `https://yeybluihiblysywmazen.supabase.co/storage/v1/object/public/images/${email}/2/${filename}`;
            setProducts(prev => [...prev, { imageIndex: filename, url, product_name: '', product_price: '', product_description: '' }]);
        }
        setUploadingProducts(false);
        e.target.value = '';
    };

    const handleSaveProduct = async (imageIndex, name, price, description) => {
        await updateStoreProduct(email, imageIndex, name, price, description);
        setProducts(prev => prev.map(p => p.imageIndex === imageIndex ? { ...p, product_name: name, product_price: price, product_description: description } : p));
    };

    const handleDeleteProduct = async (imageIndex) => {
        await deleteStoreProducts(email, [imageIndex]);
        setProducts(prev => prev.filter(p => p.imageIndex !== imageIndex));
    };

    const itemLimit = isSubscribed ? PAID_ITEMS : FREE_ITEMS;

    if (loading) return <div className="manage-loading">Loading store…</div>;

    return (
        <>
            {showUpgrade && <UpgradeModal email={email} onDismiss={() => setShowUpgrade(false)} />}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Offers */}
                <div className="manage-card">
                    <h2 className="manage-card-title">
                        <span className="store-section-title">Offers <span className="store-section-badge">sale</span></span>
                    </h2>
                    <p style={{ fontSize: 13, color: '#9C8677', margin: '0 0 16px' }}>Highlight discounted items. The original price will be shown crossed out.</p>
                    {offers.length === 0 ? <p className="manage-empty">No offers yet.</p> : (
                        <div className="items-grid" style={{ marginBottom: 16 }}>
                            {offers.map(item => <OfferCard key={item.imageIndex} item={item} currency={currency} onSave={handleSaveOffer} onDelete={handleDeleteOffer} />)}
                        </div>
                    )}
                    <input ref={offersInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAddOffer} />
                    <button
                        className="small-btn success"
                        onClick={() => { if (checkLimit(offers.length)) offersInputRef.current.click(); }}
                        disabled={uploadingOffers || offers.length >= PAID_ITEMS}
                    >
                        {uploadingOffers ? 'Uploading…' : `+ Add offer${!isSubscribed ? ` (${offers.length}/${itemLimit})` : ''}`}
                    </button>
                </div>

                {/* Products */}
                <div className="manage-card">
                    <h2 className="manage-card-title">
                        <span className="store-section-title">New products <span className="store-section-badge">featured</span></span>
                    </h2>
                    <p style={{ fontSize: 13, color: '#9C8677', margin: '0 0 16px' }}>Showcase your latest or featured products.</p>
                    {products.length === 0 ? <p className="manage-empty">No products yet.</p> : (
                        <div className="items-grid" style={{ marginBottom: 16 }}>
                            {products.map(item => <ProductCard key={item.imageIndex} item={item} currency={currency} onSave={handleSaveProduct} onDelete={handleDeleteProduct} />)}
                        </div>
                    )}
                    <input ref={productsInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAddProduct} />
                    <button
                        className="small-btn success"
                        onClick={() => { if (checkLimit(products.length)) productsInputRef.current.click(); }}
                        disabled={uploadingProducts || products.length >= PAID_ITEMS}
                    >
                        {uploadingProducts ? 'Uploading…' : `+ Add product${!isSubscribed ? ` (${products.length}/${itemLimit})` : ''}`}
                    </button>
                </div>
            </div>
            <ManagedServiceBanner email={email} />
        </>
    );
};

export default StoreManager;
